<template>
  <div>
    <v-row
      dense
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="6"
        md="5"
      >
        <v-text-field
          v-model="search"
          label="Buscar usuário"
          placeholder="Nome, e-mail ou CPF..."
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
          @keyup.enter="reload(true)"
          @click:clear="onClear"
        />
      </v-col>
      <v-col
        cols="6"
        sm="3"
      >
        <v-select
          v-model="enabledFilter"
          :items="enabledOptions"
          label="Status"
          outlined
          dense
          hide-details
          @change="reload(true)"
        />
      </v-col>
      <v-col
        cols="6"
        sm="3"
      >
        <v-select
          v-model="assocStatus"
          :items="assocOptions"
          label="Associação"
          outlined
          dense
          clearable
          hide-details
          @change="reload(true)"
        />
      </v-col>
      <v-col
        cols="12"
        md="1"
        class="d-flex align-center justify-md-end"
      >
        <v-btn
          color="primary"
          small
          block
          @click="reload(true)"
        >
          <v-icon small>
            mdi-refresh
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="total"
      :options.sync="options"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      item-key="id"
      class="elevation-0"
      dense
    >
      <template #item.cpf="{ item }">
        {{ formatCpf(item.cpf) }}
      </template>
      <template #item.telefones="{ item }">
        <div
          v-if="item.celular || item.telefone"
          class="py-1 text-caption text-no-wrap"
        >
          <div
            v-if="item.celular"
            class="d-flex align-center"
          >
            <v-icon
              x-small
              class="mr-1 grey--text"
            >
              mdi-cellphone
            </v-icon>
            <span>{{ formatPhone(item.celular) }}</span>
          </div>
          <div
            v-if="item.telefone"
            class="d-flex align-center"
            :class="{ 'mt-1': !!item.celular }"
          >
            <v-icon
              x-small
              class="mr-1 grey--text"
            >
              mdi-phone
            </v-icon>
            <span>{{ formatPhone(item.telefone) }}</span>
          </div>
        </div>
        <span
          v-else
          class="grey--text"
        >—</span>
      </template>
      <template #item.especialidades="{ item }">
        <div
          v-if="item.especialidades && item.especialidades.length"
          class="py-1"
        >
          <div
            v-for="(esp, idx) in item.especialidades"
            :key="idx"
            :class="{ 'mb-1': idx < item.especialidades.length - 1 }"
          >
            <div class="font-weight-medium text-caption">
              {{ esp.especialidade || 'Sem especialidade' }}
            </div>
            <div
              v-if="formatCrm(esp)"
              class="text-caption grey--text text--darken-2"
            >
              <v-icon
                x-small
                class="mr-1 grey--text"
              >
                mdi-card-account-details-outline
              </v-icon>
              <span>{{ formatCrm(esp) }}</span>
            </div>
          </div>
        </div>
        <span
          v-else
          class="grey--text"
        >—</span>
      </template>
      <template #item.habilitado="{ item }">
        <v-chip
          :color="item.habilitado ? 'success' : 'grey'"
          x-small
          dark
        >
          {{ item.habilitado ? 'Ativo' : 'Desativado' }}
        </v-chip>
      </template>
      <template #item.status_organizacao="{ item }">
        <v-chip
          v-if="item.status_organizacao"
          :color="assocColor(item.status_organizacao)"
          x-small
          dark
        >
          {{ item.status_organizacao }}
        </v-chip>
        <span
          v-else
          class="grey--text"
        >—</span>
      </template>
      <template #item.criado_em="{ item }">
        {{ formatDate(item.criado_em) }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import moment from 'moment'
  import { $k } from '@/lib/kraken'

  const ENABLED_OPTIONS = [
    { text: 'Todos', value: null },
    { text: 'Ativos', value: true },
    { text: 'Desativados', value: false },
  ]

  const ASSOC_OPTIONS = [
    { text: 'Ativa', value: 'ativa' },
    { text: 'Desativada', value: 'desativada' },
    { text: 'Pendente', value: 'pendente' },
    { text: 'Recusada', value: 'recusada' },
  ]

  export default {
    name: 'UsersTab',

    props: {
      org: { type: Object, required: true },
    },

    data: () => ({
      search: '',
      enabledFilter: null,
      assocStatus: null,
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 10 },
      loading: false,
      headers: [
        { text: 'ID', value: 'id', width: 70 },
        { text: 'Nome', value: 'nome' },
        { text: 'E-mail', value: 'email' },
        { text: 'CPF', value: 'cpf', width: 130 },
        { text: 'Telefones', value: 'telefones', sortable: false },
        { text: 'Especialidades / CRM', value: 'especialidades', sortable: false },
        { text: 'Status', value: 'habilitado', width: 100 },
        { text: 'Status na org', value: 'status_organizacao', width: 120 },
        { text: 'Criado em', value: 'criado_em', width: 110 },
      ],
      enabledOptions: ENABLED_OPTIONS,
      assocOptions: ASSOC_OPTIONS,
    }),

    watch: {
      options: {
        deep: true,
        handler () { this.reload() },
      },
      'org.id' () {
        this.reload(true)
      },
    },

    mounted () {
      this.reload(true)
    },

    methods: {
      formatDate (v) {
        if (!v) return '—'
        const m = moment(v)
        return m.isValid() ? m.format('DD/MM/YYYY') : v
      },
      formatPhone (v) {
        if (!v) return '—'
        const digits = String(v).replace(/\D/g, '')
        if (digits.length === 11) {
          return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
        }
        if (digits.length === 10) {
          return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
        }
        return v
      },
      formatCpf (cpf) {
        if (!cpf) return '—'
        const digits = String(cpf).replace(/\D/g, '')
        if (digits.length === 11) {
          return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        }
        return cpf
      },
      formatCrm (esp) {
        if (!esp) return ''
        const parts = []
        const council = esp.conselho || 'CRM'
        if (esp.numero_conselho) {
          const uf = esp.estado ? `/${esp.estado}` : ''
          parts.push(`${council}${uf} ${esp.numero_conselho}`)
        } else if (esp.conselho) {
          parts.push(esp.conselho)
        }
        if (esp.rqe) {
          parts.push(`RQE ${esp.rqe}`)
        }
        return parts.join(' • ')
      },
      assocColor (status) {
        switch (status) {
          case 'ativa': return 'success'
          case 'pendente': return 'warning'
          case 'recusada': return 'error'
          case 'desativada': return 'grey'
          default: return 'grey'
        }
      },
      onClear () {
        this.search = ''
        this.reload(true)
      },
      async reload (resetPage = false) {
        if (!this.org?.id) return
        if (resetPage) this.options = { ...this.options, page: 1 }
        this.loading = true
        try {
          const skip = (this.options.page - 1) * this.options.itemsPerPage
          const payload = { skip, limit: this.options.itemsPerPage, idOrganizacao: this.org.id }
          if (this.search) payload.q = this.search
          if (this.enabledFilter !== null) payload.habilitado = this.enabledFilter
          if (this.assocStatus) payload.statusAssociacao = this.assocStatus
          const res = await $k.manage.clinic_one.users.get(payload)
          this.items = res.results || []
          this.total = res.total || 0
        } catch (err) {
          console.error('[users.get]', err)
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>
